/**
 * useVipManagement 커스텀 훅
 * VIP 관리 상태 관리 및 데이터 로딩
 */
import { useState, useEffect, useCallback } from 'react';
import type { VipMember, TierConfig, VipStatistics, ActiveTab } from '../models/VipManagement';
import { vipManagementRepository } from '../repositories/VipManagementRepository';

export const useVipManagement = () => {
  // 공통 상태
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('members');

  // 회원 관리 상태
  const [members, setMembers] = useState<VipMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<VipMember | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState<string>('');
  const [sortBy, setSortBy] = useState('total_spent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // 등급 설정 상태
  const [tierConfigs, setTierConfigs] = useState<TierConfig[]>([]);
  const [editingTier, setEditingTier] = useState<string | null>(null);

  // 통계 상태
  const [statistics, setStatistics] = useState<VipStatistics | null>(null);

  // 모달 상태
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  const [showTierEdit, setShowTierEdit] = useState(false);
  const [showPointAdjust, setShowPointAdjust] = useState(false);

  /**
   * VIP 회원 목록 로딩
   */
  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vipManagementRepository.getMembers({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(filterTier && { tier_level: filterTier }),
        ...(searchTerm && { search: searchTerm }),
      });
      setMembers(data);
      setError(null);
    } catch (err) {
      console.error('VIP 회원 목록 로딩 실패:', err);
      setError('VIP 회원 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, filterTier, searchTerm]);

  /**
   * 등급 설정 로딩
   */
  const loadTierConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vipManagementRepository.getTierConfigs();
      setTierConfigs(data);
      setError(null);
    } catch (err) {
      console.error('등급 설정 로딩 실패:', err);
      setError('등급 설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * VIP 통계 로딩
   */
  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vipManagementRepository.getStatistics();
      setStatistics(data);
      setError(null);
    } catch (err) {
      console.error('VIP 통계 로딩 실패:', err);
      setError('VIP 통계를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 탭 변경 시 데이터 로딩
   */
  useEffect(() => {
    if (activeTab === 'members') {
      loadMembers();
    } else if (activeTab === 'tiers') {
      loadTierConfigs();
    } else if (activeTab === 'statistics') {
      loadStatistics();
    }
  }, [activeTab, currentPage, sortBy, sortOrder, filterTier, loadMembers, loadTierConfigs, loadStatistics]);

  /**
   * 검색어 변경 시 디바운싱
   */
  useEffect(() => {
    if (activeTab === 'members') {
      const delayedSearch = setTimeout(() => {
        setCurrentPage(1);
        loadMembers();
      }, 500);
      return () => clearTimeout(delayedSearch);
    }
  }, [searchTerm, activeTab, loadMembers]);

  /**
   * 현재 탭 데이터 새로고침
   */
  const refreshCurrentTab = useCallback(() => {
    if (activeTab === 'members') loadMembers();
    else if (activeTab === 'tiers') loadTierConfigs();
    else if (activeTab === 'statistics') loadStatistics();
  }, [activeTab, loadMembers, loadTierConfigs, loadStatistics]);

  /**
   * 정렬 옵션 변경
   */
  const handleSortChange = useCallback((sortValue: string) => {
    const [field, order] = sortValue.split('-');
    setSortBy(field);
    setSortOrder(order as 'asc' | 'desc');
  }, []);

  /**
   * 회원 상세 보기
   */
  const viewMemberDetail = useCallback((member: VipMember) => {
    setSelectedMember(member);
    setShowMemberDetail(true);
  }, []);

  /**
   * 포인트 조정 열기
   */
  const openPointAdjust = useCallback((member: VipMember) => {
    setSelectedMember(member);
    setShowPointAdjust(true);
  }, []);

  /**
   * 등급 편집 열기
   */
  const openTierEdit = useCallback((tierLevel: string) => {
    setEditingTier(tierLevel);
    setShowTierEdit(true);
  }, []);

  return {
    // 공통 상태
    loading,
    error,
    activeTab,
    setActiveTab,

    // 회원 관리
    members,
    selectedMember,
    searchTerm,
    setSearchTerm,
    filterTier,
    setFilterTier,
    sortBy,
    sortOrder,
    currentPage,
    setCurrentPage,
    handleSortChange,
    viewMemberDetail,
    openPointAdjust,

    // 등급 설정
    tierConfigs,
    editingTier,
    openTierEdit,

    // 통계
    statistics,

    // 모달 상태
    showMemberDetail,
    setShowMemberDetail,
    showTierEdit,
    setShowTierEdit,
    showPointAdjust,
    setShowPointAdjust,

    // 액션
    refreshCurrentTab,
  };
};
